import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, KeyboardEvent, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Card,
  Col,
  Form,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  ArrowLeftRight,
  ArrowRepeat,
  ArrowsMove,
  ClipboardCheck,
  ClipboardPlus,
  ExclamationTriangleFill,
  HouseFill,
  InfoCircleFill,
  Plus,
  Question,
  QuestionCircleFill,
  Radar,
  Search,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { congregationApi, useCongSettings } from "../../../api/cong";
import QueryKeys from "../../../api/queryKeys";
import { useUsers } from "../../../api/user";
import { useWMSpeakers, weekendApi } from "../../../api/weekend";
import { HGBugsnagNotify } from "../../../helpers/bugsnag";
import { localizedDayOfWeek } from "../../../helpers/dateHelpers";
import { ErrType, getErrorType, getStatusCode } from "../../../helpers/errors";
import HourglassGlobals, { HGContext } from "../../../helpers/globals";
import { containsFuzzy, t } from "../../../helpers/locale";
import { nameOfUser } from "../../../helpers/user";
import { getGUID, mergePastedShareURL, NBSP, shallowEqualObjects, sleep } from "../../../helpers/util";
import { updateSpeakerCache, updateWMCongregationCache } from "../../../query/meetings";
import {
  CongDetails,
  Congregation,
  NewCongInput,
  PublicSpeakerInput,
  PublicTalk,
  Speaker,
  SpeakerCongMappingInput,
  SpeakerDirectExchange,
} from "../../../types/scheduling/weekend";
import User, { UserAppt } from "../../../types/user";
import { ValidationErrorDetail } from "../../../types/validation";
import { Danger } from "../../alerts";
import { DeleteButton, EditButton } from "../../buttons";
import { QueryStatus } from "../../queryStatus";
import { congShareHelp, meetingSearchWindow } from "../../register/congInfo";
import RouteLeavingGuard, { WarningDialog } from "../../routeLeavingGuard";
import { SaveResult, SaveStatus } from "../../saveStatus";
import { SearchFilter } from "../../searchFilter";
import { congCompare, CongName, ShowStatus, speakerCompare, Status } from "../common";
import { HourglassTimePicker } from "../date";
import { getMyCongAndGroupIds } from "../../../helpers/cong";
import { BlockAcceptModal, canUpdateWeekendSchedules, CongWeekendMeetingTime, SpeakerName } from "./wm_common";
import { AddSpeakerModal } from "./wm_cong_speakers_add";
import useRunOnce from "../../../helpers/useRunOnce";
import { DayOfWeekPicker } from "../../utils";
import { SpeakerSearchModal } from "./components/modals/SpeakerSearch";
import { SMPWNewCongInput } from "../../../types/smpw";
import { smpwAPI } from "../../../api/smpw";
import { updateSMPWCongregations } from "../../../query/smpw";
import { RegistrationState } from "../../../types/register/registerState";
import { CongregationDropdown } from "./components";

export function CongSpeakers(props: { langGroupId: number }) {
  const { t } = useTranslation();
  const myCong = useContext(HGContext).globals.cong!;
  const [cong, setCong] = useState({} as Congregation);
  const [speaker, setSpeaker] = useState({} as Speaker);
  const [tempSpeaker, setTempSpeaker] = useState({} as Speaker);
  const [saveResult, setSaveResult] = useState(SaveResult.None);
  const [showTalkModal, setShowTalkModal] = useState(false);
  const [showAddCongModal, setShowAddCongModal] = useState(false);
  const [showAddSpeakerModal, setShowAddSpeakerModal] = useState(false);
  const [showLocalSpeakerWarning, setShowLocalSpeakerWarning] = useState(false);
  const [showDeleteCongModal, setShowDeleteCongModal] = useState(false);
  const [showMoveSpeakerModal, setShowMoveSpeakerModal] = useState(false);
  const [showDeleteSpeakerModal, setShowDeleteSpeakerModal] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesEditModeEnabled, setNotesEditModeEnabled] = useState(false);
  const [enableEditNameMode, setEnableEditNameMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showDirtyWarning, setShowDirtyWarning] = useState(false);
  const [pendingSpeaker, setPendingSpeaker] = useState<Speaker>();
  const [speakerIsDirect, setSpeakerIsDirect] = useState(false);
  const [validationErrors, setValidationErrors] = useState<(keyof Speaker)[]>([]);
  const [hasASMTs, setHasASMTs] = useState(false);
  const [congFilter, setCongFilter] = useState("");
  const [speakerFilter, setSpeakerFilter] = useState("");
  const [showSpeakerSearchModal, setShowSpeakerSearchModal] = useState(false);
  const [showCongMappingModal, setShowCongMappingModal] = useState(false);
  const allSpeakersQuery = useQuery({
    queryKey: [QueryKeys.WMAllSpeakers, props.langGroupId],
    queryFn: () => weekendApi.getSpeakersAll(props.langGroupId),
    enabled: showSpeakerSearchModal,
  });

  const congregationQuery = useQuery({
    queryKey: [QueryKeys.WMCongregations, props.langGroupId],
    queryFn: () => weekendApi.getCongregations(props.langGroupId),
  });
  const talksQuery = useQuery({
    queryKey: [QueryKeys.WMTalks],
    queryFn: () => weekendApi.getTalks(undefined, props.langGroupId),
    meta: { clearStoredLangGroupOn404: true },
  });
  const speakerQuery = useWMSpeakers(cong.id, props.langGroupId, false, {
    meta: { clearStoredLangGroupOn404: true },
  });
  const usersQuery = useUsers();
  const queryClient = useQueryClient();
  const [scrollPos, setScrollPos] = useState(0);

  useEffect(() => {
    setDirty(!shallowEqualObjects(speaker, tempSpeaker));
  }, [speaker, tempSpeaker]);

  const queryStatus = QueryStatus(talksQuery, congregationQuery, speakerQuery, usersQuery);
  if (queryStatus !== null) return queryStatus;
  if (!talksQuery.data || !congregationQuery.data || !speakerQuery.data || !usersQuery.data) return null;

  const myCongAndGroupIds = getMyCongAndGroupIds();

  const handleSpeakerInfoUpdate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationErrors([]);

    const form = e.currentTarget;
    if (!form.checkValidity()) return;

    const output: PublicSpeakerInput = {
      id: tempSpeaker.id,
      congregation: tempSpeaker.congregation?.id ?? null,
      congregation_name: tempSpeaker.congregation_name,
      firstname: tempSpeaker.firstname,
      lastname: tempSpeaker.lastname,
      middlename: tempSpeaker.middlename,
      suffix: tempSpeaker.suffix,
      descriptor: tempSpeaker.descriptor,
      email: tempSpeaker.email,
      cellphone: tempSpeaker.cellphone,
      homephone: tempSpeaker.homephone,
      otherphone: tempSpeaker.otherphone,
      appt: tempSpeaker.appt,
      out: tempSpeaker.out,
      public_talks: tempSpeaker.public_talks,
    };

    try {
      const savedSpeaker = await weekendApi.setSpeaker(output, undefined, props.langGroupId);
      setSaveResult(SaveResult.Success);
      updateSpeakerCache(queryClient, cong.id, props.langGroupId, tempSpeaker, savedSpeaker);
      setSpeaker(savedSpeaker);
      setTempSpeaker(savedSpeaker);
      setDirty(false);
    } catch (err: any) {
      setSaveResult(SaveResult.Failure);
      if (getStatusCode(err) === 409) {
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.WMSpeakers, cong.id] });
        return;
      }
      console.error("error updating public speaker", err);
      const errType = await getErrorType(err);
      switch (errType) {
        case ErrType.Validation: {
          const respData = await err.response.clone().json();
          if (Array.isArray(respData?.errorDetails)) {
            const validationFailures = respData.errorDetails.map((ve: ValidationErrorDetail) => ve.name);
            setValidationErrors(validationFailures);
          }
          break;
        }
        default:
          HGBugsnagNotify("wmSetSpeaker", err);
      }
    }
  };

  const newSpeaker = (s: Speaker, force?: boolean) => {
    if (dirty && force !== true) {
      setShowDirtyWarning(true);
      setPendingSpeaker(s);
    } else {
      setSpeaker(s);
      setTempSpeaker(s);
      setSaveResult(SaveResult.None);
      setDirty(false);
      setSpeakerIsDirect(!!s.userId && !usersQuery.data.some((u) => u.id === s.userId));
    }
  };

  const disableEdit = myCongAndGroupIds.has(cong.id) || speakerIsDirect || !canUpdateWeekendSchedules();
  const disableEditOutlines = myCongAndGroupIds.has(cong.id) && !!props.langGroupId && props.langGroupId !== myCong.id;

  type SpeakerErrors = { [K in keyof Speaker]?: boolean };
  const validErrors: SpeakerErrors = {};
  validationErrors.forEach((ve) => {
    const vek = ve as keyof Speaker;
    validErrors[vek] = true;
  });

  // not a func because of rendering issues
  const SpeakerInfo = (
    <Form onSubmit={handleSpeakerInfoUpdate}>
      <Row>
        <Form.Group className="mb-4 col-12 col-md-4" controlId="speakerInfoFirstName">
          <Form.Label>{t("general.first-name")}</Form.Label>
          <Form.Control
            disabled={disableEdit}
            isInvalid={validErrors.firstname}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, firstname: e.target.value });
            }}
            value={tempSpeaker.firstname}
          />
        </Form.Group>

        <Form.Group className="mb-4 col-12 col-md-3" controlId="speakerInfoMiddleName">
          <Form.Label>{t("userinfo.name.middle")}</Form.Label>
          <Form.Control
            disabled={disableEdit}
            isInvalid={validErrors.middlename}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, middlename: e.target.value });
            }}
            value={tempSpeaker.middlename ?? ""}
          />
        </Form.Group>

        <Form.Group className="mb-4 col-12 col-md-4" controlId="speakerInfoLastName">
          <Form.Label>{t("userinfo.name.last")}</Form.Label>
          <Form.Control
            disabled={disableEdit}
            isInvalid={validErrors.lastname}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, lastname: e.target.value });
            }}
            value={tempSpeaker.lastname}
          />
        </Form.Group>

        <Form.Group className="mb-4 col-12 col-md" controlId="speakerInfoSuffix">
          <Form.Label>{t("userinfo.name.suffix")}</Form.Label>
          <Form.Control
            disabled={disableEdit}
            isInvalid={validErrors.suffix}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, suffix: e.target.value });
            }}
            value={tempSpeaker.suffix ?? ""}
          />
        </Form.Group>
      </Row>

      <Row>
        {HourglassGlobals.isE2E() && (
          <Form.Group className="mb-4 col-12 col-md" controlId="speakerInfoDescriptor">
            <Form.Label>
              {t("userinfo.label.0")}
              <span className="ms-2" title={t("userinfo.label.help")}>
                <QuestionCircleFill color="blue" />
              </span>
            </Form.Label>
            <Form.Control
              disabled={disableEdit}
              required
              isInvalid={validErrors.descriptor}
              onChange={(e) => {
                setTempSpeaker({ ...tempSpeaker, descriptor: e.target.value });
              }}
              value={tempSpeaker.descriptor ?? ""}
              aria-required="true"
            />
          </Form.Group>
        )}

        <Form.Group className="mb-4 col-12 col-md-3" controlId="speakerInfoEmail">
          <Form.Label>{t("userinfo.email")}</Form.Label>
          <Form.Control
            disabled={disableEdit}
            isInvalid={validErrors.email}
            type="email"
            value={tempSpeaker.email ?? ""}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, email: e.target.value });
            }}
            aria-label={t("userinfo.email")}
          />
        </Form.Group>

        <Form.Group className="mb-4 col-12 col-md-3" controlId="speakerInfoCellphone">
          <Form.Label>{t("userinfo.cellphone")}</Form.Label>
          <Form.Control
            disabled={disableEdit}
            isInvalid={validErrors.cellphone}
            type="tel"
            minLength={4}
            maxLength={20}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, cellphone: e.target.value });
            }}
            value={tempSpeaker.cellphone ?? ""}
            aria-label={t("userinfo.cellphone")}
          />
        </Form.Group>

        <Form.Group className="mb-4 col-12 col-md-3" controlId="speakerInfoHomephone">
          <Form.Label>{t("userinfo.homephone")}</Form.Label>
          <Form.Control
            disabled={disableEdit}
            isInvalid={validErrors.homephone}
            type="tel"
            minLength={4}
            maxLength={20}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, homephone: e.target.value });
            }}
            value={tempSpeaker.homephone ?? ""}
            aria-label={t("userinfo.homephone")}
          />
        </Form.Group>

        <Form.Group className="mb-4 col-12 col-md-3" controlId="speakerInfoOtherphone">
          <Form.Label>{t("userinfo.otherphone")}</Form.Label>
          <Form.Control
            disabled={disableEdit}
            isInvalid={validErrors.otherphone}
            type="tel"
            minLength={4}
            maxLength={20}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, otherphone: e.target.value });
            }}
            value={tempSpeaker.otherphone ?? ""}
            aria-label={t("userinfo.otherphone")}
          />
        </Form.Group>
      </Row>

      <Row className="mb-4">
        <Form.Group className="col-12 col-md-3" controlId="speakerInfoAppt">
          <Form.Label>{t("userinfo.appointment.0")}</Form.Label>
          <Form.Select
            disabled={disableEdit}
            isInvalid={validErrors.appt}
            value={tempSpeaker.appt ?? ""}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, appt: e.target.value });
            }}
          >
            <option />
            <option value={UserAppt.Elder}>{t("recordcard.elder")}</option>
            <option value={UserAppt.MS}>{t("recordcard.ministerialservant")}</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="col-12 col-md-3" controlId="speakerInfoCong">
          <Form.Label>{t("list.congregation.title")}</Form.Label>
          <Form.Control
            disabled={!!tempSpeaker.congregation}
            minLength={4}
            maxLength={128}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, congregation_name: e.target.value });
            }}
            value={
              tempSpeaker.congregation_name ??
              tempSpeaker.congregation?.custom_name ??
              tempSpeaker.congregation?.name ??
              ""
            }
          />
        </Form.Group>

        <Form.Group className="col-12 col-md-6">
          {/*for spacing to line up with the appointment box*/}
          <Form.Label>&nbsp;</Form.Label>
          <Select
            className="react-select-container col mb-1"
            classNamePrefix="react-select"
            placeholder={t("schedules.weekend.outlines")}
            isDisabled={speakerIsDirect || !canUpdateWeekendSchedules() || disableEditOutlines}
            closeMenuOnSelect={false}
            isMulti={true}
            isClearable={false}
            styles={{
              //@ts-ignore weird type error with typescript 5.3.3
              menu: (s) => ({ ...s, zIndex: 1500 }),
            }}
            value={
              tempSpeaker.public_talks
                ?.sort((a, b) => a.number - b.number)
                .map((t) => {
                  return { value: t.number, label: `${t.number}` };
                }) || []
            }
            options={
              talksQuery.data
                ?.sort((a, b) => a.number - b.number)
                .map((t) => {
                  return { value: t.number, label: `${`${t.number}`.padStart(3, NBSP)} — ${t.title}` };
                }) || []
            }
            onChange={(e) => {
              setTempSpeaker({
                ...tempSpeaker,
                public_talks: e.map((t) => {
                  return { number: t.value } as PublicTalk;
                }),
              });
            }}
          />
          {!disableEditOutlines && (
            <Form.Text
              muted
              className="cursor-pointer"
              onClick={() => {
                setShowTalkModal(true);
              }}
            >
              {t("schedules.weekend.talk-bulk")}
            </Form.Text>
          )}
        </Form.Group>
      </Row>

      <ButtonToolbar className="justify-content-between mt-2">
        <ButtonGroup>
          <Button type="submit" variant="primary" disabled={!dirty || speakerIsDirect || !canUpdateWeekendSchedules()}>
            {t("general.save")}
          </Button>
          <Button
            variant="secondary"
            disabled={!dirty || speakerIsDirect || !canUpdateWeekendSchedules()}
            onClick={() => {
              setTempSpeaker(speaker);
            }}
          >
            {t("general.reset")}
          </Button>
          <Button
            disabled={disableEdit}
            variant="secondary"
            onClick={() => {
              setShowMoveSpeakerModal(true);
            }}
          >
            {t("schedules.weekend.move-assignment")}
          </Button>
          <Button
            disabled={disableEdit}
            variant="danger"
            onClick={() => {
              setShowDeleteSpeakerModal(true);
            }}
          >
            {t("general.delete")}
          </Button>
          <SaveStatus saveResult={saveResult} saveKey="wm_speaker" setSaveResult={setSaveResult} />
        </ButtonGroup>
        <h5>
          <Form.Check
            disabled={disableEdit}
            type="switch"
            label={t("schedules.weekend.outgoing-talks")}
            checked={tempSpeaker.out}
            onChange={(e) => {
              setTempSpeaker({ ...tempSpeaker, out: e.target.checked });
            }}
          />
        </h5>
      </ButtonToolbar>
    </Form>
  );

  const deleteSuccess = () => {
    setSpeaker({} as Speaker);
    setTempSpeaker({} as Speaker);
    setShowDirtyWarning(false);
  };

  const possibleMoveTargets = congregationQuery.data
    ?.filter((c) => {
      if (!speaker) return false;
      if (c.direct_exchange === SpeakerDirectExchange.Yes) return false;
      if (c.id === speaker.congregation?.id) return false;
      return !myCongAndGroupIds.has(c.id);
    })
    .sort(congCompare);

  return (
    <>
      <RouteLeavingGuard
        when={dirty}
        modalTitle={t("popup.lose-changes.title")}
        modalBody={t("popup.lose-changes.body", {
          unsaved_type: t("schedules.weekend.speaker-info"),
        })}
        modalCancel={t("general.cancel")}
        modalConfirm={t("popup.lose-changes.button.dont-save")}
      />
      <WarningDialog
        show={showDirtyWarning}
        title={t("popup.lose-changes.title")}
        content={t("popup.lose-changes.body", {
          unsaved_type: t("schedules.weekend.speaker-info"),
        })}
        cancelButtonText={t("general.cancel")}
        confirmButtonText={t("popup.lose-changes.button.dont-save")}
        onCancel={() => {
          setShowDirtyWarning(false);
          setPendingSpeaker(undefined);
        }}
        onConfirm={() => {
          if (pendingSpeaker) newSpeaker(pendingSpeaker, true);
          setPendingSpeaker(undefined);
          setShowDirtyWarning(false);
        }}
      />
      <TalksModal
        key={`${tempSpeaker.id}_${showTalkModal}`}
        tempSpeaker={tempSpeaker}
        show={showTalkModal}
        setShow={setShowTalkModal}
        setTempSpeaker={setTempSpeaker}
        canSave={!speakerIsDirect}
      />
      <DeleteCongModel
        cong={cong}
        show={showDeleteCongModal}
        setShow={setShowDeleteCongModal}
        setCong={setCong}
        refresh={congregationQuery.refetch}
        hasASMTs={hasASMTs}
        langGroupId={props.langGroupId}
      />
      <MoveSpeakerModal
        speaker={speaker}
        show={showMoveSpeakerModal}
        setShow={setShowMoveSpeakerModal}
        congregations={possibleMoveTargets}
        moveSuccess={async () => {
          setSpeaker({} as Speaker);
          setTempSpeaker({} as Speaker);
          await speakerQuery.refetch();
        }}
        langGroupId={props.langGroupId}
      />
      <DeleteSpeakerModel
        speaker={speaker}
        show={showDeleteSpeakerModal}
        setShow={setShowDeleteSpeakerModal}
        deleteSuccess={deleteSuccess}
        refresh={speakerQuery.refetch}
        langGroupId={props.langGroupId}
      />
      <AddCongModal show={showAddCongModal} setShow={setShowAddCongModal} langGroupId={props.langGroupId} />
      <AddSpeakerModal
        cong={cong}
        show={showAddSpeakerModal}
        setShow={setShowAddSpeakerModal}
        refresh={speakerQuery.refetch}
        newSpeaker={newSpeaker}
        langGroupId={props.langGroupId}
      />
      <LocalSpeakerWarning show={showLocalSpeakerWarning} setShow={setShowLocalSpeakerWarning} />
      <SpeakerCongMappingModal
        show={showCongMappingModal}
        setShow={setShowCongMappingModal}
        speakers={speakerQuery.data}
        congregations={congregationQuery.data}
        langGroupId={props.langGroupId}
        onExecuteSuccess={async () => {
          await speakerQuery.refetch();
          await congregationQuery.refetch();
        }}
      />
      <SpeakerSearchModal
        show={showSpeakerSearchModal}
        setShow={setShowSpeakerSearchModal}
        speakers={allSpeakersQuery.data ?? []}
        setSelectedTalk={() => {
          return;
        }}
        setSelectedSpeaker={(speakerId) => {
          const speaker = allSpeakersQuery.data?.find((s) => s.id === speakerId);
          if (speaker) newSpeaker(speaker);
        }}
        setSelectedCong={(congId) => {
          if (congId === -1) {
            setCong({ id: -1 } as Congregation);
            return;
          }
          const cong = congregationQuery.data?.find((c) => c.id === congId);
          if (cong) setCong(cong);
        }}
        setOverrideCong={() => {
          return;
        }}
      />
      <Row>
        {!!cong.id && (cong.id > 0 || !!speaker.id) && (
          <Card>
            <Card.Body>
              {!!cong.id && !speaker.id ? (
                <CongInfo
                  cong={cong}
                  notes={notes}
                  notesEditModeEnabled={notesEditModeEnabled}
                  enableEditNameMode={enableEditNameMode}
                  setShowDeleteCongModal={setShowDeleteCongModal}
                  setNotes={setNotes}
                  setNotesEditModeEnabled={setNotesEditModeEnabled}
                  setEnableEditNameMode={setEnableEditNameMode}
                  refresh={congregationQuery.refetch}
                  setHasASMTs={setHasASMTs}
                  langGroupId={props.langGroupId}
                />
              ) : speaker.id ? (
                SpeakerInfo
              ) : (
                ""
              )}
            </Card.Body>
          </Card>
        )}
      </Row>
      <Row className="d-flex justify-content-center mt-2 mb-2" xs="auto">
        <Button variant="outline-primary" onClick={() => setShowSpeakerSearchModal(true)}>
          <Search className="me-1" />
          {t("search.hint")}
        </Button>
      </Row>
      <Row>
        <Col>
          <h4 className="d-flex justify-content-between">
            {t("schedules.weekend.congregations")}
            <AddButton setShow={setShowAddCongModal} />
          </h4>
          <SearchFilter className="mb-1" setFilter={setCongFilter} filter={congFilter} />
          <CongList
            congs={
              congregationQuery.data.filter((c) => {
                return containsFuzzy(c.custom_name ?? c.name, congFilter);
              }) ?? []
            }
            scrollPos={scrollPos}
            setScrollPos={setScrollPos}
            cong={cong}
            setCong={setCong}
            setNotes={setNotes}
            setSpeaker={setSpeaker}
            setTempSpeaker={setTempSpeaker}
            setNotesEditModeEnabled={setNotesEditModeEnabled}
            setEnableEditNameMode={setEnableEditNameMode}
            langGroupId={props.langGroupId}
          />
        </Col>
        <Col>
          {!!cong.id && (
            <>
              <div className="d-flex justify-content-between">
                <div>
                  <h4>{t("schedules.weekend.cong-speakers")}</h4>
                  {cong.direct_exchange === SpeakerDirectExchange.Yes && (
                    <Alert variant="info">
                      <InfoCircleFill size={16} className="me-1" />
                      {t("schedules.weekend.speakers-managed", { cong: CongName(cong) })}
                    </Alert>
                  )}
                </div>
                {cong.id === -1 && speakerQuery.data.length > 20 && (
                  <div className="mb-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowCongMappingModal(true)}>
                      <ArrowsMove className="me-1" />
                      {t("schedules.weekend.congregations")}
                    </Button>
                  </div>
                )}
                {cong.direct_exchange !== SpeakerDirectExchange.Yes && (
                  <AddButton
                    setShow={!myCongAndGroupIds.has(cong.id) ? setShowAddSpeakerModal : setShowLocalSpeakerWarning}
                  />
                )}
              </div>
              <SearchFilter className="mb-1" setFilter={setSpeakerFilter} filter={speakerFilter} />

              <SpeakerList
                speakers={speakerQuery.data.filter((s) => containsFuzzy(nameOfUser(s), speakerFilter)) ?? []}
                users={usersQuery.data}
                speaker={speaker}
                newSpeaker={newSpeaker}
              />
            </>
          )}
        </Col>
      </Row>
    </>
  );
}

type UnlistedCong = {
  name: string;
  url: string;
  wmdow: number;
  wmtime: string;
};
const blankUnlisted: UnlistedCong = { name: "", url: "", wmdow: 7, wmtime: "10:00" };
const allowedUnlistedCountries = new Set(["br", "cu"]);

export const AddCongModal = (props: {
  show: boolean;
  setShow: (show: boolean) => void;
  langGroupId: number;

  // used for SMPW registration
  smpw?: boolean;
  noModal?: boolean;
  regState?: RegistrationState;
  setRegState?: (set: RegistrationState) => void;
  smpwOnNext?: () => void;
  setShowManaged?: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const [inputText, setInputText] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [saveResult, setSaveResult] = useState(SaveResult.None);
  const [errorMessage, setErrorMessage] = useState("");
  const [enableUnlisted, setEnableUnlisted] = useState(false);
  const [enableCountryUnlistedSearch, setEnableCountryUnlistedSearch] = useState(false);
  const [countryUnlistedCongNumber, setCountryUnlistedCongNumber] = useState("");
  const [unlisted, setUnlisted] = useState<UnlistedCong>(blankUnlisted);
  const queryClient = useQueryClient();
  const myCong = useContext(HGContext).globals.cong;
  const isUnlistedAllowed = myCong ? allowedUnlistedCountries.has(myCong.country.code.toLowerCase()) : false;

  const handleClose = () => {
    props.setShow(false);
    setShowSpinner(false);
    setSaveResult(SaveResult.None);
    setErrorMessage("");
    setUnlisted(blankUnlisted);
    setEnableUnlisted(false);
    setEnableCountryUnlistedSearch(false);
    setCountryUnlistedCongNumber("");
  };

  const handleAutoAddCongregations = async () => {
    await weekendApi.autoAddCongregations();
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WMCongregations],
      }),
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WMSpeakers],
      }),
    ]);
    handleClose();
  };

  const handleUpdate = async () => {
    setShowSpinner(true);
    const input: NewCongInput = {
      guid: getCongGUID(),
      name: unlisted.name,
      weekendMeeting: {
        weekday: unlisted.wmdow,
        time: unlisted.wmtime,
      },
      notListed: enableUnlisted,
    };
    try {
      const resp = await weekendApi.addCong(input, props.langGroupId);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMCongregations, props.langGroupId],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMSpeakers, resp.congregationId],
        }),
      ]);
      setSaveResult(SaveResult.Success);
      setInputText("");
      handleClose();
    } catch (err: any) {
      setSaveResult(SaveResult.Failure);
      switch (getStatusCode(err)) {
        case 409:
          setErrorMessage(t("schedules.weekend.cong-already-added"));
          setInputText("");
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.WMCongregations, props.langGroupId],
            }),
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.WMSpeakers],
            }),
          ]);
          break;
        default:
          console.error("error adding weekend cong", err);
          HGBugsnagNotify("addWMCong", err);
      }
    } finally {
      setShowSpinner(false);
    }
  };

  const handleSmpwUpdate = async () => {
    setShowSpinner(true);
    const input: SMPWNewCongInput = {
      guid: getCongGUID(),
    };
    try {
      const resp = await smpwAPI.addCongregations(input, true);
      if (!resp.unmanaged) {
        // SMPW can only put publishers in unmanaged congregations: one that uses Hourglass manages its own publishers
        setSaveResult(SaveResult.Failure);
        if (props.setShowManaged) {
          props.setShowManaged();
        } else {
          setErrorMessage(t("smpw.congregation_manages_publishers"));
        }
        return;
      }
      updateSMPWCongregations(queryClient, resp);
      await sleep(750);
      setSaveResult(SaveResult.Success);
      await sleep(750);
      setInputText("");
      if (props.smpwOnNext) props.smpwOnNext();
    } catch (err: any) {
      setSaveResult(SaveResult.Failure);
      if (getStatusCode(err) === 409) {
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.SMPWNearbyCongregations] });
      } else {
        console.error("error adding smpw cong", err);
        HGBugsnagNotify("addSMPWCong", err);
      }
    } finally {
      setShowSpinner(false);
    }
  };

  const findCountryUnlisted = async () => {
    setSaveResult(SaveResult.None);
    const congNumber = Number(countryUnlistedCongNumber);
    if (!congNumber) return;
    setShowSpinner(true);
    try {
      const resp = await congregationApi.getCountryUnlisted(congNumber);
      setUnlisted({ ...unlisted, name: resp.name, url: resp.guid });
      setInputText(resp.guid);
    } catch (err: any) {
      setSaveResult(SaveResult.Failure);
      setUnlisted(blankUnlisted);
      setInputText("");
      switch (getStatusCode(err)) {
        case 429:
          break;
        default:
          console.error("error in country unlisted search", err);
          HGBugsnagNotify("addWMCong_unlisted", err);
      }
    } finally {
      setShowSpinner(false);
    }
  };

  const getCongGUID = (): string => {
    const value = props.setRegState && props.regState ? props.regState.cong.shareText : inputText;
    return getGUID(value);
  };

  const unlistedComplete = (): boolean => {
    return !!unlisted.name && !!unlisted.wmdow && !!unlisted.wmtime;
  };

  const applyShareText = (input: string) => {
    if (input.trim().toLowerCase() === "jw.org=skip") {
      setEnableUnlisted(true);
      setErrorMessage("");
      setSaveResult(SaveResult.None);
      return;
    }
    props.setRegState && props.regState
      ? props.setRegState({ ...props.regState, cong: { ...props.regState.cong, shareText: input } })
      : setInputText(input);
    if (!input) {
      setSaveResult(SaveResult.None);
      setErrorMessage("");
      return;
    }
    if (getGUID(input)) {
      setSaveResult(SaveResult.None);
      setErrorMessage("");
    } else {
      setSaveResult(SaveResult.Failure);
      setErrorMessage(t("popup.error.title.invalid-input"));
    }
  };

  const body = (
    <>
      <Modal.Header closeButton>
        <Modal.Title className="pb-3">
          {props.smpw ? t("smpw.your-congregation") : t("schedules.weekend.add-congregation")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!enableUnlisted && !enableCountryUnlistedSearch && (
          <>
            <Button variant="info" size="sm" onClick={congShareHelp} aria-label={t("nav.help")}>
              <Question color="white" size={16} />
            </Button>
            <Button variant="primary" size="sm" className="ms-2" onClick={() => meetingSearchWindow()}>
              {t("search.hint")}
            </Button>
            {isUnlistedAllowed && (
              <Button
                variant="secondary"
                size="sm"
                className="ms-2"
                onClick={() => {
                  setEnableUnlisted(true);
                  setEnableCountryUnlistedSearch(true);
                }}
              >
                {t("register.cong-not-listed.title")}
              </Button>
            )}
            {!props.smpw && (
              <Button
                variant="success"
                size="sm"
                className="ms-2"
                onClick={handleAutoAddCongregations}
                title={t("schedules.weekend.auto-add")}
                aria-label={t("schedules.weekend.auto-add")}
              >
                <Radar size="1.5em" />
              </Button>
            )}
            <Form.Control
              as="textarea"
              className="mt-2"
              value={props.setRegState && props.regState ? props.regState.cong.shareText : inputText}
              onChange={(e) => applyShareText(e.currentTarget.value)}
              onPaste={(e) => {
                const merged = mergePastedShareURL(e.clipboardData);
                if (!merged) return;
                e.preventDefault();
                const target = e.currentTarget;
                const current = target.value;
                const start = target.selectionStart ?? current.length;
                const end = target.selectionEnd ?? current.length;
                applyShareText(current.slice(0, start) + merged + current.slice(end));
              }}
              style={{ height: "10em" }}
              placeholder={t("register.cong-share")}
            />
          </>
        )}
        {enableUnlisted && (
          <div>
            {enableCountryUnlistedSearch && (
              <div>
                <Form.Group as={Row}>
                  <Col xs={8}>
                    <Form.Control
                      required
                      placeholder={t("register.cong-number")}
                      type="number"
                      value={countryUnlistedCongNumber}
                      onChange={(e) => setCountryUnlistedCongNumber(e.currentTarget.value)}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button variant="primary" onClick={findCountryUnlisted} disabled={!countryUnlistedCongNumber}>
                      {t("search.hint")}
                    </Button>
                  </Col>
                </Form.Group>
                {!!unlisted.name && <p className="mt-1 fw-bold">{unlisted.name}</p>}
              </div>
            )}
            {!enableCountryUnlistedSearch && (
              <>
                <Form.Control
                  required
                  disabled={enableCountryUnlistedSearch}
                  placeholder={t("congprofile.name")}
                  onChange={(e) => setUnlisted({ ...unlisted, name: e.currentTarget.value })}
                />
                <Form.Control
                  className="mt-1"
                  required
                  disabled={enableCountryUnlistedSearch}
                  placeholder="URL"
                  onChange={(e) => {
                    const url = e.currentTarget.value;
                    setUnlisted({ ...unlisted, url: url });
                    setInputText(url);
                  }}
                ></Form.Control>
              </>
            )}
            <p className="mt-3 mb-1">{t("conganalysis.attendance.weekend")}</p>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Select
                    required
                    value={unlisted.wmdow}
                    onChange={(e) => setUnlisted({ ...unlisted, wmdow: parseInt(e.currentTarget.value) })}
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map((dow) => (
                      <option key={dow} value={dow}>
                        {localizedDayOfWeek(dow, i18n.language)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <HourglassTimePicker
                time={unlisted.wmtime}
                onTimeChange={(newTime: string) => setUnlisted({ ...unlisted, wmtime: newTime })}
              />
            </Row>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Form.Text className="warn_text" hidden={!errorMessage}>
          {errorMessage}
        </Form.Text>
        {showSpinner && <Spinner size="sm" animation="border" />}
        {!showSpinner && <SaveStatus saveResult={saveResult} saveKey="wm_cong" setSaveResult={setSaveResult} />}
        <Button
          className="mt-2"
          variant="primary"
          disabled={!!errorMessage || !getCongGUID() || (enableUnlisted && !unlistedComplete()) || showSpinner}
          onClick={props.smpw ? handleSmpwUpdate : handleUpdate}
        >
          {t("general.save")}
        </Button>
      </Modal.Footer>
    </>
  );

  return props.noModal ? (
    <div>{body}</div>
  ) : (
    <Modal show={props.show} onHide={handleClose} className="mom">
      {body}
    </Modal>
  );
};

const LocalSpeakerWarning = (props: { show: boolean; setShow: (show: boolean) => void }) => {
  const { t } = useTranslation();
  return (
    <Modal show={props.show} onHide={() => props.setShow(false)}>
      <Modal.Header closeButton>
        <Modal.Title>{t("schedules.weekend.local-speakers")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{t("schedules.weekend.local-speakers-body")}</Modal.Body>
    </Modal>
  );
};

const DeleteCongModel = (props: {
  cong: Congregation;
  show: boolean;
  setShow: (show: boolean) => void;
  setCong: (cong: Congregation) => void;
  refresh: () => void;
  hasASMTs: boolean;
  langGroupId: number;
}) => {
  const { t } = useTranslation();
  const handleClose = () => props.setShow(false);
  const handleDelete = async () => {
    weekendApi.deleteCong(props.cong.id, props.langGroupId).then((d) => {
      if (d.Deleted) {
        props.setCong({} as Congregation);
        handleClose();
        props.refresh();
      }
    });
  };

  return (
    <Modal show={props.show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("general.delete")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{t("schedules.weekend.delete-cong-confirm")}</p>
        <p>
          <b>{CongName(props.cong)}</b>
        </p>
        {props.hasASMTs && (
          <Danger
            text={
              <>
                <ExclamationTriangleFill /> {t("schedules.weekend.delete-cong-warning")}
              </>
            }
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("general.cancel")}
        </Button>
        <Button variant="danger" onClick={handleDelete}>
          {t("general.delete")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const MoveSpeakerModal = (props: {
  speaker: Speaker;
  show: boolean;
  setShow: (show: boolean) => void;
  congregations: Congregation[];
  moveSuccess: () => void;
  langGroupId: number;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [targetCong, setTargetCong] = useState<Congregation>();
  const [inFlight, setInFlight] = useState(false);
  const setSpeakerMutation = useMutation({
    mutationFn: (ps: PublicSpeakerInput) => weekendApi.setSpeaker(ps, undefined, props.langGroupId),
  });

  const handleClose = () => {
    setTargetCong(undefined);
    setInFlight(false);
    props.setShow(false);
  };

  const selectCong = (selected: number) => {
    const selectedCong = props.congregations.find((c) => c.id === selected);
    if (!selectedCong) return;
    setTargetCong(selectedCong);
  };

  const handleMove = async () => {
    if (!targetCong || !targetCong.id) return;
    // you can't move a speaker to the Unknown congregation. So any time we move them, null out the congregation name to avoid confusion
    const updatedSpeaker: PublicSpeakerInput = {
      ...props.speaker,
      congregation: targetCong.id,
      congregation_name: null,
    };
    setInFlight(true);
    try {
      await setSpeakerMutation.mutateAsync(updatedSpeaker);
      props.moveSuccess();
      handleClose();
    } catch (err: any) {
      if (getStatusCode(err) === 409) {
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.WMSpeakers] });
        handleClose();
      } else {
        HGBugsnagNotify("moveSpeaker", err);
      }
    } finally {
      setInFlight(false);
    }
  };

  return (
    <Modal show={props.show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("schedules.weekend.move-assignment")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CongregationDropdown congs={props.congregations} setCong={selectCong} autoFocus />
      </Modal.Body>
      <Modal.Footer>
        {inFlight && <Spinner animation="border" />}
        <Button variant="secondary" onClick={handleClose}>
          {t("general.cancel")}
        </Button>
        <Button variant="primary" onClick={handleMove} disabled={!targetCong || inFlight}>
          {t("schedules.weekend.move-assignment")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const DeleteSpeakerModel = (props: {
  speaker: Speaker;
  show: boolean;
  setShow: (show: boolean) => void;
  refresh: () => void;
  deleteSuccess: () => void;
  langGroupId: number;
}) => {
  const { t } = useTranslation();
  const handleClose = () => props.setShow(false);
  const handleDelete = async () => {
    weekendApi.deleteSpeaker(props.speaker.id, props.langGroupId).then((d) => {
      if (d.Deleted) {
        handleClose();
        props.refresh();
        props.deleteSuccess();
      }
    });
  };

  return (
    <Modal show={props.show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("general.delete")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {t("schedules.weekend.delete-speaker-confirm")}
        <p>
          <b>{nameOfUser(props.speaker, HourglassGlobals.nameFmt)}</b>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("general.cancel")}
        </Button>
        <Button variant="danger" onClick={handleDelete}>
          {t("general.delete")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AddButton = (props: { setShow: (set: boolean) => void }) => {
  const { t } = useTranslation();

  return (
    <Button
      variant="primary"
      size="sm"
      className="ms-2"
      disabled={!canUpdateWeekendSchedules()}
      onClick={() => {
        props.setShow(true);
      }}
      aria-label={t("nav.publishers.add")}
    >
      <Plus size="1.5em" />
    </Button>
  );
};

const TalksModal = (props: {
  tempSpeaker: Speaker;
  show: boolean;
  setShow: (set: boolean) => void;
  setTempSpeaker: (set: Speaker) => void;
  canSave: boolean;
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [talks, setTalks] = useState(
    (props.tempSpeaker.public_talks ?? []).map((t: PublicTalk) => `${t.number}`).join(", ") ?? "",
  );
  const [invalid, setInvalid] = useState(false);

  const cleanList = (a: string[]) => {
    const seen: Record<string, boolean> = {};

    // remove dupes
    const filtered = a.filter((item) => {
      return Object.hasOwn(seen, item) ? false : (seen[item] = true);
    });

    // convert to numbers
    const numbers = filtered.map((n) => {
      return Number(n);
    });

    // put in order
    numbers.sort((a: number, b: number) => {
      return a - b;
    });
    return numbers;
  };

  return (
    <Modal show={props.show} onHide={() => props.setShow(false)}>
      <Modal.Header closeButton>
        <Modal.Title>
          {t("schedules.weekend.talks-for", {
            speaker: nameOfUser(props.tempSpeaker, HourglassGlobals.nameFmt),
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          as="textarea"
          autoFocus
          defaultValue={talks}
          onChange={(e) => {
            if (e.target.value.match("^[0-9, \n]*$") != null) {
              setInvalid(false);
            } else {
              setInvalid(true);
            }
            setTalks(e.target.value);
          }}
        />
        <div className="warn_text" hidden={!invalid}>
          {t("schedules.weekend.talk-list-warning")}
        </div>
        <Form.Text muted>{t("schedules.weekend.talk-bulk-separated")}</Form.Text>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={copied ? "success" : "secondary"}
          onClick={async () => {
            await navigator.clipboard.writeText(talks);
            setCopied(true);
          }}
        >
          {copied ? <ClipboardCheck color="white" size="1.2em" /> : <ClipboardPlus size="1.2em" />}
          <span className="ms-1">{t("general.copy")}</span>
        </Button>
        {props.canSave && (
          <Button
            variant="primary"
            onClick={() => {
              props.setTempSpeaker({
                ...props.tempSpeaker,
                public_talks: cleanList(
                  talks
                    .replace(/[^0-9\s]+/g, " ")
                    .replace(/\n+/g, " ")
                    .replace(/\s\s+/g, " ")
                    .split(" "),
                ).map((t) => {
                  return { number: Number(t) } as PublicTalk;
                }),
              });
              props.setShow(false);
            }}
          >
            {t("popup.error.button.ok")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const CongInfo = (props: {
  cong: Congregation;
  notes: string;
  notesEditModeEnabled: boolean;
  enableEditNameMode: boolean;
  setShowDeleteCongModal: (set: boolean) => void;
  setNotes: (set: string) => void;
  setNotesEditModeEnabled: (set: boolean) => void;
  setEnableEditNameMode: (set: boolean) => void;
  refresh: () => void;
  setHasASMTs: (set: boolean) => void;
  langGroupId: number;
}) => {
  const { t } = useTranslation();
  const myCong = useContext(HGContext).globals.cong!;
  const [status, setStatus] = useState(Status.Null);
  const [refreshStatus, setRefreshStatus] = useState(Status.Null);
  const [customName, setCustomName] = useState(props.cong.custom_name ?? "");
  const [domParser] = useState(new DOMParser());
  const [wmDow, setWmDow] = useState(props.cong.wm_dow);
  const [wmTime, setWmTime] = useState(props.cong.wm_time ?? "");

  const settings = useCongSettings(props.langGroupId);
  const queryClient = useQueryClient();
  const queryStatus = QueryStatus(settings);
  if (queryStatus !== null) return queryStatus;

  const handleNameChange = async () => {
    if (customName !== props.cong.name) {
      setStatus(Status.Pending);

      if (myCong.id === props.cong.id && settings.data) {
        try {
          const newSettings = await congregationApi.setSettings(
            {
              ...settings.data,
              congregation_display_name: customName,
            },
            props.langGroupId,
          );
          queryClient.setQueryData([QueryKeys.CongregationSettings, props.langGroupId], newSettings);
          setStatus(Status.Null);
          props.setEnableEditNameMode(false);
        } catch (err: any) {
          if (getStatusCode(err) === 409) {
            await queryClient.invalidateQueries({
              queryKey: [QueryKeys.CongregationSettings, props.langGroupId],
            });
            setStatus(Status.Null);
            props.setEnableEditNameMode(false);
          } else {
            setStatus(Status.Failure);
            HGBugsnagNotify("setMyCustomWMName", err);
          }
        }
      } else {
        try {
          const details: CongDetails = {
            custom_name: customName,
            notes: props.notes,
          };
          if (wmDow !== props.cong.wm_dow) details.wm_dow = wmDow;
          if (wmTime !== props.cong.wm_time) details.wm_time = wmTime;
          await weekendApi.setCongDetails(props.cong.id, details, props.langGroupId);
          props.refresh();
          setStatus(Status.Null);
          props.setEnableEditNameMode(false);
        } catch (err: any) {
          if (getStatusCode(err) === 409) {
            props.refresh();
            setStatus(Status.Null);
            props.setEnableEditNameMode(false);
          } else {
            setStatus(Status.Failure);
            HGBugsnagNotify("setWMCongNotes", err);
          }
        }
      }
    } else {
      setCustomName(props.cong.custom_name ?? "");
    }
  };

  const handleKeyPress = async (e: KeyboardEvent) => {
    // noinspection AllyPlainJsInspection
    if (e.key === "Enter") {
      await handleNameChange();
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between">
        <div>
          {props.enableEditNameMode ? (
            <Form.Control
              placeholder={props.cong.name}
              autoFocus
              value={customName || props.cong.name}
              onKeyUp={handleKeyPress}
              onChange={(e) => {
                setCustomName(e.target.value);
              }}
            />
          ) : (
            <h4>
              {customName || props.cong.name}
              <EditButton
                size="sm"
                disabled={!canUpdateWeekendSchedules()}
                className="ms-2"
                onClick={() => {
                  props.setEnableEditNameMode(true);
                }}
              />
              <Button
                size="sm"
                variant="primary"
                className="ms-1"
                aria-label={t("schedules.meetings.time-from-jworg")}
                onClick={async () => {
                  setRefreshStatus(Status.Pending);
                  const jworgcong = await congregationApi.getCongInfoFromJWOrg(props.cong.guid);
                  await queryClient.invalidateQueries({
                    queryKey: [QueryKeys.WMCongregations, props.langGroupId],
                  });
                  if (jworgcong && jworgcong.properties) {
                    // since the state doesn't filter down cleanly, we backfill it
                    props.cong.wm_dow = jworgcong.properties.schedule.current.weekend.weekday;
                    props.cong.wm_time = jworgcong.properties.schedule.current.weekend.time;
                    props.cong.future_wm_dow = jworgcong.properties.schedule.future.weekend.weekday;
                    props.cong.future_wm_time = jworgcong.properties.schedule.future.weekend.time;
                    props.cong.address = jworgcong.properties.address;
                    props.cong.phone =
                      Array.isArray(jworgcong.properties.phones) && jworgcong.properties.phones[0]
                        ? jworgcong.properties.phones[0].phone
                        : "";
                    props.cong.name = jworgcong.properties.orgName;
                  }
                  setRefreshStatus(Status.Null);
                }}
              >
                {refreshStatus === Status.Pending ? (
                  <ShowStatus status={refreshStatus} size="sm" />
                ) : (
                  <ArrowRepeat size="1.5em" />
                )}
              </Button>
            </h4>
          )}
          <i>{props.cong.address}</i>
          {/*we need this parsing because some of the phone numbers from jw.org have things like &nbsp; embedded in them*/}
          <div>{domParser.parseFromString(props.cong.phone, "text/html")?.body?.textContent ?? null}</div>
          {props.enableEditNameMode && props.cong.times_editable ? (
            <Row className="mt-1">
              <Col>
                <DayOfWeekPicker size="sm" value={wmDow} setValue={setWmDow} />
              </Col>
              <HourglassTimePicker time={wmTime} onTimeChange={setWmTime} />
            </Row>
          ) : (
            <CongWeekendMeetingTime cong={props.cong} />
          )}
          {!!props.cong.ptc_info && (
            <div className="mt-2">
              <div className="bold">{t("schedules.settings.ptc-contact")}</div>
              <div className="pre-regular">{props.cong.ptc_info}</div>
            </div>
          )}
          {props.cong.direct_exchange === SpeakerDirectExchange.Pending && (
            <p className="mt-4">
              <i>{t("schedules.weekend.speaker-trade.pending")}</i>
            </p>
          )}
        </div>
        {props.cong.id !== myCong.id && (
          <Form.Group className="cong_notes">
            <Form.Control
              disabled={!props.enableEditNameMode}
              as="textarea"
              rows={3}
              value={props.notes}
              onChange={(e) => props.setNotes(e.target.value)}
              style={{ width: "25em", height: "10em" }}
              placeholder={t("schedules.weekend.notes.other-cong")}
            />
          </Form.Group>
        )}
        {props.cong.id !== myCong.id && (
          <div>
            <DeleteButton
              disabled={!canUpdateWeekendSchedules()}
              text={t("general.delete")}
              onClick={async () => {
                const res = await weekendApi.getCongTrades(props.cong.id, props.langGroupId);
                props.setHasASMTs(!!(res.inbound.length + res.outbound.length));
                props.setShowDeleteCongModal(true);
              }}
            />
          </div>
        )}
      </div>
      {props.enableEditNameMode && (
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              setCustomName(props.cong.custom_name ?? "");
              props.setEnableEditNameMode(false);
            }}
            disabled={status === Status.Pending}
          >
            {t("general.cancel")}
          </Button>
          <Button variant="primary" onClick={handleNameChange} disabled={status === Status.Pending}>
            {t("general.save")}
          </Button>
          {!!status && <ShowStatus status={status} />}
        </div>
      )}
    </div>
  );
};

const SpeakerCongMappingModal = (props: {
  show: boolean;
  setShow: (show: boolean) => void;
  speakers: Speaker[];
  congregations: Congregation[];
  langGroupId: number;
  onExecuteSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const storageKey = `hg_wm_cong_mapping_${props.langGroupId}`;

  const [mapping, setMapping] = useState<Record<string, number | null>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [inFlight, setInFlight] = useState(false);
  const [executeResult, setExecuteResult] = useState(SaveResult.None);

  const uniqueCongNames = useMemo(() => {
    const names = new Set<string>();
    props.speakers.forEach((s) => {
      if (s.congregation_name) names.add(s.congregation_name);
    });
    return Array.from(names).sort();
  }, [props.speakers]);

  // exclude direct-exchange congregations and own congregation from mapping targets
  const availableCongs = useMemo(() => {
    const myCongAndGroupIds = getMyCongAndGroupIds();
    return props.congregations
      .filter((c) => c.direct_exchange !== SpeakerDirectExchange.Yes && !myCongAndGroupIds.has(c.id))
      .sort(congCompare);
  }, [props.congregations]);

  const updateMapping = (newMapping: Record<string, number | null>) => {
    setMapping(newMapping);
    localStorage.setItem(storageKey, JSON.stringify(newMapping));
  };

  const handleClose = () => {
    setExecuteResult(SaveResult.None);
    props.setShow(false);
  };

  const handleExecute = async () => {
    const inputs: SpeakerCongMappingInput[] = Object.entries(mapping)
      .filter(([, targetId]) => targetId !== null && targetId > 0)
      .map(([name, targetId]) => ({
        congregation_name: name,
        target_congregation_id: targetId!,
      }));

    if (!inputs.length) return;
    setInFlight(true);
    setExecuteResult(SaveResult.None);
    try {
      const batchSize = 20;
      for (let i = 0; i < inputs.length; i += batchSize) {
        await weekendApi.mapSpeakerCongregations(inputs.slice(i, i + batchSize), props.langGroupId);
      }
      setExecuteResult(SaveResult.Success);
      updateMapping({});
      props.onExecuteSuccess();
      props.setShow(false);
    } catch (err: any) {
      if (getStatusCode(err) === 409) {
        updateMapping({});
        props.onExecuteSuccess();
      } else {
        setExecuteResult(SaveResult.Failure);
        HGBugsnagNotify("mapSpeakerCongregations", err);
      }
    } finally {
      setInFlight(false);
    }
  };

  const mappedCount = Object.entries(mapping).filter(([, v]) => v !== null && v > 0).length;

  return (
    <Modal show={props.show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("schedules.weekend.move-assignment")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ListGroup variant="flush">
          {uniqueCongNames.map((name) => {
            const speakerCount = props.speakers.filter((s) => s.congregation_name === name).length;
            return (
              <ListGroup.Item key={name} className="px-0">
                <Row className="align-items-start gy-1">
                  <Col xs={12} sm={5} className="pt-4">
                    <div className="fw-semibold">{name}</div>
                    <div className="text-muted small">
                      {t("schedules.weekend.cong-speakers")}{" "}
                      <Badge bg="secondary" pill>
                        {speakerCount}
                      </Badge>
                    </div>
                  </Col>
                  <Col xs={12} sm={7}>
                    <CongregationDropdown
                      congs={availableCongs}
                      override={mapping[name] ?? undefined}
                      setCong={(id) => updateMapping({ ...mapping, [name]: id })}
                      hideMeetingTime
                      hideDOW
                    />
                  </Col>
                </Row>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        {inFlight && <Spinner size="sm" animation="border" />}
        <SaveStatus saveResult={executeResult} saveKey="cong_mapping_execute" setSaveResult={setExecuteResult} />
        <Button variant="secondary" onClick={handleClose}>
          {t("general.cancel")}
        </Button>
        <Button variant="primary" disabled={mappedCount === 0 || inFlight} onClick={handleExecute}>
          {t("schedules.weekend.move-assignment")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const SpeakerList = (props: {
  speakers: Speaker[];
  speaker: Speaker;
  newSpeaker: (set: Speaker) => void;
  users: User[];
}) => {
  const speakers = props.speakers.length
    ? props.speakers.sort(speakerCompare).map((s) => {
        // if we have a userId, we'll show the display name of the user (otherwise, with e2e, we'd have nothing to show)
        const user = s.userId ? props.users.find((u) => u.id === s.userId) : undefined;
        const directUser = s.userId && !user;
        const speakerOrUser: Speaker | User = user ?? s;
        return (
          <ListGroup.Item
            className="d-flex justify-content-between align-items-start"
            action
            key={s.id}
            active={s.id === props.speaker.id}
            onClick={() => props.newSpeaker(s)}
          >
            <SpeakerName speaker={speakerOrUser} notOutOverride={!s.out} />
            {directUser && <DirectExchangeIcon status={SpeakerDirectExchange.Yes} />}
          </ListGroup.Item>
        );
      })
    : [
        <ListGroup.Item className="d-flex justify-content-between align-items-start" action key="none" disabled={true}>
          <span className="text-muted fst-italic">{t("general.none")}</span>
        </ListGroup.Item>,
      ];

  return (
    <div className="max-height-50vh overflow-y-auto">
      <ListGroup>{speakers}</ListGroup>
    </div>
  );
};

const CongList = (props: {
  congs: Congregation[];
  cong: Congregation;
  setCong: (set: Congregation) => void;
  setNotes: (set: string) => void;
  setSpeaker: (set: Speaker) => void;
  setTempSpeaker: (set: Speaker) => void;
  setNotesEditModeEnabled: (set: boolean) => void;
  setEnableEditNameMode: (set: boolean) => void;
  langGroupId: number;
  scrollPos: number;
  setScrollPos: (pos: number) => void;
}) => {
  const { t } = useTranslation();
  const myCong = useContext(HGContext).globals.cong!;
  const [showBlockAccept, setShowBlockAccept] = useState(false);
  const acceptExchangeMutation = useMutation({
    mutationFn: (response: { congId: number; accept: boolean }) =>
      weekendApi.respondSpeakerExchange(response.congId, response.accept, props.langGroupId),
  });
  const queryClient = useQueryClient();
  const sortedCongs = props.congs.sort(congCompare);
  const proposedCongs = sortedCongs.filter((c) => c.direct_exchange === SpeakerDirectExchange.Proposed);
  const localSpeakersQuery = useWMSpeakers(myCong.id, props.langGroupId);

  const divId = "hg-cong-scroll-div";

  useRunOnce(() => {
    if (props.scrollPos) {
      const el = document.getElementById(divId);
      if (el) {
        el.scrollTo(0, props.scrollPos);
      }
    }
  });

  const canAccept = (): boolean => {
    // must have at least one local speaker who can go out who has at least one outline
    return localSpeakersQuery.data?.filter((s) => s.out).some((s) => s.public_talks.length > 0) ?? false;
  };

  const response = async (cong: Congregation, accept: boolean) => {
    if (accept && !canAccept()) {
      // not allowed to accept: they haven't set up their speakers
      setShowBlockAccept(true);
      return;
    }
    // find the cong in the pending state - it has all the info like meeting times. the "proposed" one doesn't
    const pendingCong =
      props.congs.find((c) => c.id === cong.id && c.direct_exchange !== SpeakerDirectExchange.Proposed) || cong;
    try {
      await acceptExchangeMutation.mutateAsync({ congId: cong.id, accept: accept });
      const newCong: Congregation = {
        ...pendingCong,
        direct_exchange: accept ? SpeakerDirectExchange.Yes : SpeakerDirectExchange.No,
      };
      updateWMCongregationCache(queryClient, cong, newCong, props.langGroupId);
    } catch (err: any) {
      if (getStatusCode(err) === 409) {
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.WMCongregations, props.langGroupId] });
      } else {
        console.error("error accepting speaker exchange", err);
        HGBugsnagNotify("acceptWMSpeakerExchange", err);
      }
    }
  };

  const congClickScroll = () => {
    const el = document.getElementById(divId);
    if (el) {
      props.setScrollPos(el.scrollTop);
    }
  };

  return (
    <div className="max-height-50vh overflow-y-auto" id={divId}>
      <BlockAcceptModal show={showBlockAccept} setShow={setShowBlockAccept} />
      {proposedCongs.length > 0 && (
        <>
          <b>{t("schedules.weekend.speaker-trade.cong-list")}</b>
          <ListGroup>
            {proposedCongs.map((c) => (
              <ListGroup.Item key={c.id} className="d-flex align-items-center">
                <span className="w-100">{c.name}</span>
                <span className="flex-shrink-1 me-2">
                  <Button
                    size="sm"
                    variant="success"
                    onClick={() => response(c, true)}
                    disabled={!canUpdateWeekendSchedules()}
                  >
                    {t("schedules.assignment.accept")}
                  </Button>
                </span>
                <span className="flex-shrink-1">
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => response(c, false)}
                    disabled={!canUpdateWeekendSchedules()}
                  >
                    {t("schedules.assignment.decline")}
                  </Button>
                </span>
              </ListGroup.Item>
            ))}
          </ListGroup>
          <hr />
        </>
      )}
      <ListGroup>
        <ListGroup.Item
          key={null}
          action
          className="d-flex justify-content-between align-items-start"
          active={props.cong.id === -1}
          onClick={() => {
            props.setCong({ id: -1 } as Congregation); // using -1 instead of null to respect type safety
            props.setNotes("");
            props.setSpeaker({} as Speaker);
            props.setTempSpeaker({} as Speaker);
            props.setNotesEditModeEnabled(false);
            props.setEnableEditNameMode(false);
            congClickScroll();
          }}
        >
          <div className="fst-italic">&lt;{t("report-list.month.error")}&gt;</div>
        </ListGroup.Item>
        {sortedCongs
          .filter((c) => c.direct_exchange !== SpeakerDirectExchange.Proposed)
          .map((c) => (
            <ListGroup.Item
              key={c.id}
              action
              className="d-flex justify-content-between align-items-start"
              active={c.id === props.cong.id}
              onClick={() => {
                props.setCong(c);
                props.setNotes(c.notes);
                props.setSpeaker({} as Speaker);
                props.setTempSpeaker({} as Speaker);
                props.setNotesEditModeEnabled(false);
                props.setEnableEditNameMode(false);
                congClickScroll();
              }}
            >
              <div>
                {CongName(c)}
                {!!c.custom_name && c.custom_name !== c.name && <div className="text-muted smallText">{c.name}</div>}
              </div>
              <CongIcon isMyCong={getMyCongAndGroupIds().has(c.id)} directStatus={c.direct_exchange} />
            </ListGroup.Item>
          ))}
      </ListGroup>
    </div>
  );
};

function CongIcon(props: { isMyCong: boolean; directStatus: SpeakerDirectExchange }) {
  if (props.isMyCong) return <HouseFill />;
  if (props.directStatus) return <DirectExchangeIcon status={props.directStatus} />;
  return null;
}

function DirectExchangeIcon(props: { status?: SpeakerDirectExchange }) {
  if (!props.status || props.status === SpeakerDirectExchange.No) return null;
  return <ArrowLeftRight className={props.status === SpeakerDirectExchange.Yes ? "active-speaker-link" : undefined} />;
}
